import type {ReactNode} from 'react';
import {useRef, useEffect, useCallback} from 'react';

export type SyncHeightRenderProps = {
    setFixedRef: (el: HTMLElement | null) => void,
    setScrolledRef: (el: HTMLElement | null) => void,
};

export type SyncHeightProps = {
    children: (props: SyncHeightRenderProps) => ReactNode,
};

export const SyncHeight = ({children}: SyncHeightProps) => {
    const fixedElRef = useRef<HTMLElement | null>(null);
    const scrolledElRef = useRef<HTMLElement | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    const syncHeight = useCallback(() => {
        const fixed = fixedElRef.current;
        const scrolled = scrolledElRef.current;

        if (!fixed || !scrolled) {
            return;
        }

        const h = scrolled.clientHeight || 0;

        if (fixed.style.height !== `${h}px`) {
            fixed.style.height = `${h}px`;
            scrolled.style.height = `${h}px`;
        }
    }, []);

    const ensureObserver = useCallback(() => {
        if (!observerRef.current && typeof ResizeObserver !== 'undefined') {
            observerRef.current = new ResizeObserver(() => {
                if (rafIdRef.current !== null) {
                    cancelAnimationFrame(rafIdRef.current);
                }
                rafIdRef.current = requestAnimationFrame(syncHeight);
            });
        }
    }, [syncHeight]);

    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
            if (observerRef.current && scrolledElRef.current) {
                observerRef.current.unobserve(scrolledElRef.current);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const setFixedRef = useCallback((el: HTMLElement | null) => {
        if (el && fixedElRef.current !== el) {
            fixedElRef.current = el;
            syncHeight();
        }
    }, [syncHeight]);

    const setScrolledRef = useCallback((el: HTMLElement | null) => {
        if (el && scrolledElRef.current !== el) {
            if (observerRef.current && scrolledElRef.current) {
                observerRef.current.unobserve(scrolledElRef.current);
            }
            scrolledElRef.current = el;
            ensureObserver();
            if (observerRef.current) {
                observerRef.current.observe(el);
            }
            syncHeight();
        }
    }, [ensureObserver, syncHeight]);

    return children({setFixedRef, setScrolledRef});
};
